import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { InteractionRequiredAuthError, PublicClientApplication, type AccountInfo } from '@azure/msal-browser';
import { Body1, Button, MessageBar, Spinner, Title2, makeStyles, tokens } from '@fluentui/react-components';
import { parseRoles, type CurrentUser } from '@lg/shared';
import type { AppConfig } from '../config';
import { AuthContext, type AuthState } from './AuthContext';

const useStyles = makeStyles({
  page: {
    minHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalM,
  },
});

function toUser(account: AccountInfo): CurrentUser {
  const claims = account.idTokenClaims ?? {};
  return {
    upn: account.username.toLowerCase(),
    name: account.name ?? account.username,
    // Roles in the ID token are for UI gating only; the API enforces them.
    roles: parseRoles(claims['roles']),
  };
}

type Phase =
  { kind: 'checking' } | { kind: 'needsSignIn'; error?: string } | { kind: 'signedIn'; account: AccountInfo };

/**
 * Sign-in inside the D365 iframe (spec 15.1): cached account → ssoSilent
 * (reuses the Entra session from D365) → Sign in button with loginPopup.
 * Never redirects, because redirects break inside an iframe.
 */
export function MsalAuthGate({ entra, children }: { entra: AppConfig['entra']; children: ReactNode }) {
  const styles = useStyles();
  const scopes = useMemo(() => [entra.apiScope], [entra.apiScope]);
  const msal = useMemo(
    () =>
      new PublicClientApplication({
        auth: {
          clientId: entra.clientId,
          authority: `https://login.microsoftonline.com/${entra.tenantId}`,
          redirectUri: `${window.location.origin}/`,
        },
        cache: { cacheLocation: 'sessionStorage' },
      }),
    [entra.clientId, entra.tenantId],
  );
  const [phase, setPhase] = useState<Phase>({ kind: 'checking' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await msal.initialize();
      const cached = msal.getAllAccounts()[0];
      if (cached) {
        try {
          await msal.acquireTokenSilent({ account: cached, scopes });
          if (!cancelled) setPhase({ kind: 'signedIn', account: cached });
          return;
        } catch {
          // fall through to ssoSilent
        }
      }
      try {
        const result = await msal.ssoSilent({ scopes });
        if (!cancelled && result.account) setPhase({ kind: 'signedIn', account: result.account });
      } catch {
        if (!cancelled) setPhase({ kind: 'needsSignIn' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [msal, scopes]);

  const signIn = async () => {
    try {
      const result = await msal.loginPopup({ scopes });
      setPhase({ kind: 'signedIn', account: result.account });
    } catch (err) {
      setPhase({ kind: 'needsSignIn', error: err instanceof Error ? err.message : 'Sign-in failed' });
    }
  };

  const state = useMemo<AuthState | null>(() => {
    if (phase.kind !== 'signedIn') return null;
    const account = phase.account;
    return {
      user: toUser(account),
      getAuthHeaders: async () => {
        try {
          const { accessToken } = await msal.acquireTokenSilent({ account, scopes });
          return { Authorization: `Bearer ${accessToken}` };
        } catch (err) {
          if (err instanceof InteractionRequiredAuthError) {
            const { accessToken } = await msal.acquireTokenPopup({ account, scopes });
            return { Authorization: `Bearer ${accessToken}` };
          }
          throw err;
        }
      },
      signOut: () => void msal.logoutPopup({ account }),
    };
  }, [phase, msal, scopes]);

  if (phase.kind === 'checking') {
    return (
      <div className={styles.page}>
        <Spinner label="Checking your Microsoft sign-in…" />
      </div>
    );
  }
  if (!state) {
    return (
      <div className={styles.page}>
        <Title2 as="h1">Venue onboarding</Title2>
        <Body1>Sign in with your work account to continue.</Body1>
        {phase.kind === 'needsSignIn' && phase.error && <MessageBar intent="error">{phase.error}</MessageBar>}
        <Button appearance="primary" onClick={signIn}>
          Sign in
        </Button>
      </div>
    );
  }
  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}
